import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { EMPTY, Observable, of, Subscription } from "rxjs";
import { finalize, map, switchMap, take } from "rxjs/operators";
import { NzNotificationService } from "ng-zorro-antd/notification";
import type {
    EvidenceModalLinkedFileRequest,
    EvidenceModalUploadRequest
} from "../components/evidence_requirements_modal/evidence_requirements_modal.component";
import {
    buildSecureStorageEvidenceItemUploadQueryParams,
    buildSecureStorageManageFileQueryParams,
    buildSecureStoragePreviewQueryParams,
    SECURE_STORAGE_MANAGE_FILES_ROUTE,
    SECURE_STORAGE_PREVIEW_FILE_ROUTE,
    SECURE_STORAGE_UPLOAD_FILES_ROUTE
} from "../utils/secure_storage_route.util";
import { AssessmentWorkItemsService } from "./assessment_work_items.service";
import type { AssessmentWorkItemEvidenceDetail } from "./assessment_work_items.types";
import { LoginService } from "./login.service";
import { SecureStorageService } from "./secure_storage.service";

export interface EvidenceFileActionContext {
    readonly isCurrent: () => boolean;
    readonly merchantId?: number;
}

function positiveId(value: unknown): value is number {
    return typeof value === "number" && Number.isSafeInteger(value) && value > 0;
}

@Injectable({ providedIn: "root" })
export class EvidenceFileActionsService {
    constructor(
        private readonly workItems: AssessmentWorkItemsService,
        private readonly secureStorage: SecureStorageService,
        private readonly login: LoginService,
        private readonly router: Router,
        private readonly notification: NzNotificationService
    ) {}

    openLinkedFile(
        productLicenseId: number,
        request: EvidenceModalLinkedFileRequest,
        context: EvidenceFileActionContext
    ): Subscription {
        const action = request?.action;
        if (!positiveId(productLicenseId) || !positiveId(request?.evidenceRequirementId) ||
            !positiveId(request?.secureStorageFileId) ||
            (action !== "download" && action !== "preview" && action !== "manage")) {
            return Subscription.EMPTY;
        }

        const isCurrent = this.currentActionGuard(context);
        if (!isCurrent()) return Subscription.EMPTY;

        const itemId = request.evidenceRequirementId;
        const fileId = request.secureStorageFileId;
        let pendingTab = action === "download" ? null : this.openPendingTab();
        return this.authorizedItem(productLicenseId, itemId, context).pipe(
            switchMap((item) => {
                if (!isCurrent() || !item.linkedFileAccessible ||
                    item.linkedSecureStorageFileId !== fileId) return EMPTY;

                if (action === "download") {
                    return this.secureStorage.getFileAccessUrl(item.merchantId, fileId).pipe(
                        switchMap((file) => this.secureStorage.downloadFileBlob(file.accessUrl).pipe(
                            map((blob) => ({ kind: "download" as const, blob, fileName: file.fileName || "linked-evidence-file" }))
                        ))
                    );
                }

                if (action === "preview") {
                    return this.secureStorage.getPreviewStatusForMerchantFile(item.merchantId, fileId).pipe(
                        map((status) => ({ kind: "preview" as const, merchantId: item.merchantId,
                            previewable: status.previewSupported && status.previewable }))
                    );
                }

                return of({ kind: "manage" as const, merchantId: item.merchantId });
            }),
            take(1),
            finalize(() => pendingTab?.close())
        ).subscribe({
            next: (result) => {
                if (!isCurrent()) return;
                if (result.kind === "download") {
                    this.downloadBlob(result.blob, result.fileName);
                } else if (result.kind === "preview") {
                    if (result.previewable) {
                        this.openRoute(SECURE_STORAGE_PREVIEW_FILE_ROUTE,
                            buildSecureStoragePreviewQueryParams(result.merchantId, fileId), pendingTab);
                        pendingTab = null;
                    } else {
                        this.notification.error("Preview unavailable", "This file is not ready for a safe preview.");
                    }
                } else {
                    this.openRoute(SECURE_STORAGE_MANAGE_FILES_ROUTE,
                        buildSecureStorageManageFileQueryParams(result.merchantId, fileId), pendingTab);
                    pendingTab = null;
                }
            },
            error: () => {
                pendingTab?.close();
                if (isCurrent()) this.notification.error("File unavailable", "You no longer have access to this linked file.");
            }
        });
    }

    openUpload(request: EvidenceModalUploadRequest, context: EvidenceFileActionContext): Subscription {
        if (!positiveId(request?.productLicenseId) || !positiveId(request?.evidenceRequirementId)) {
            return Subscription.EMPTY;
        }
        const isCurrent = this.currentActionGuard(context);
        if (!isCurrent()) return Subscription.EMPTY;

        let pendingTab = this.openPendingTab();
        return this.authorizedItem(request.productLicenseId, request.evidenceRequirementId, context)
            .pipe(take(1), finalize(() => pendingTab?.close()))
            .subscribe({
                next: (item) => {
                    if (!isCurrent() || !item.capabilities.canUpload) {
                        pendingTab?.close();
                        return;
                    }
                    this.openRoute(SECURE_STORAGE_UPLOAD_FILES_ROUTE,
                        buildSecureStorageEvidenceItemUploadQueryParams(
                            item.merchantId, request.productLicenseId, request.evidenceRequirementId), pendingTab);
                    pendingTab = null;
                },
                error: () => {
                    pendingTab?.close();
                    if (isCurrent()) this.notification.error("Upload unavailable", "You no longer have access to this evidence item.");
                }
            });
    }

    private authorizedItem(productLicenseId: number, itemId: number,
        context: EvidenceFileActionContext): Observable<AssessmentWorkItemEvidenceDetail> {
        return this.workItems.getEvidenceDetail(productLicenseId, itemId).pipe(
            map((item) => {
                if (item.productLicenseId !== productLicenseId || item.id !== itemId ||
                    !positiveId(item.merchantId) ||
                    (context.merchantId !== undefined && context.merchantId !== item.merchantId)) {
                    throw new Error("Evidence item context changed.");
                }
                return item;
            })
        );
    }

    private currentActionGuard(context: EvidenceFileActionContext): () => boolean {
        const accountId = this.login.getCurrentAccountId();
        const userId = this.login.getCurrentUserId();
        const token = this.login.getToken();
        return () => positiveId(accountId) && positiveId(userId) && !!token &&
            accountId === this.login.getCurrentAccountId() &&
            userId === this.login.getCurrentUserId() && token === this.login.getToken() && context.isCurrent();
    }

    private openPendingTab(): Window | null {
        if (typeof window === "undefined") return null;
        const tab = window.open("about:blank", "_blank");
        if (tab) tab.opener = null;
        return tab;
    }

    private openRoute(route: readonly string[], queryParams: Record<string, string>, tab: Window | null): void {
        if (!Object.keys(queryParams).length || typeof window === "undefined") return;
        const url = this.router.serializeUrl(this.router.createUrlTree([...route], { queryParams }));
        if (tab && !tab.closed) {
            tab.location.replace(url);
        } else {
            window.open(url, "_blank", "noopener,noreferrer");
        }
    }

    private downloadBlob(blob: Blob, fileName: string): void {
        if (typeof document === "undefined" || typeof window === "undefined") return;
        const objectUrl = window.URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        try {
            anchor.href = objectUrl;
            anchor.download = fileName;
            document.body.appendChild(anchor);
            anchor.click();
        } finally {
            anchor.remove();
            window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 0);
        }
    }
}
